import React, { useMemo } from 'react';
import { styled } from '@mui/material/styles';
import { createFilter, StylesConfig, components as SelectComponents, GroupBase } from 'react-select';
import { SelectOption } from './EntitySelect';
import { Typography, TextField, Chip, Paper, MenuItem } from '@mui/material';
import { emphasize } from '@mui/material/styles';
import CancelIcon from '@mui/icons-material/Cancel';
import Select, { Props as SelectProps } from 'react-select';
import { COLORS } from '../Styleguide/Common/colors';

const PREFIX = 'StyledReactSelect';

const classes = {
  dropdown: `${PREFIX}-dropdown`,
  dropdownDisabled: `${PREFIX}-dropdownDisabled`,
  input: `${PREFIX}-input`,
  valueContainer: `${PREFIX}-valueContainer`,
  chip: `${PREFIX}-chip`,
  chipFocused: `${PREFIX}-chipFocused`,
  noOptionsMessage: `${PREFIX}-noOptionsMessage`,
  singleValue: `${PREFIX}-singleValue`,
  placeholder: `${PREFIX}-placeholder`,
  placeholderDisabled: `${PREFIX}-placeholderDisabled`,
  paper: `${PREFIX}-paper`,
  divider: `${PREFIX}-divider`,
};

const StyledSelect = styled('div')(({ theme: { spacing, palette } }) => ({
  [`& .${classes.dropdown}`]: {
    marginTop: spacing(),
    marginBottom: spacing(),
    width: '100%',
  },

  [`& .${classes.dropdownDisabled}`]: {
    marginTop: spacing(),
    marginBottom: spacing(),
    width: '100%',
    backgroundColor: COLORS.black[80],
  },

  [`& .${classes.input}`]: {
    display: 'flex',
    padding: 0,
    height: 'auto',
    '& input': {
      color: 'white !important',
    },
  },

  [`& .${classes.valueContainer}`]: {
    display: 'flex',
    flexWrap: 'wrap',
    flex: 1,
    alignItems: 'center',
  },

  [`& .${classes.chip}`]: {
    margin: spacing(0.5, 0.25),
  },

  [`& .${classes.chipFocused}`]: {
    backgroundColor: emphasize(palette.mode === 'light' ? palette.grey[300] : palette.grey[700], 0.08),
  },

  [`& .${classes.noOptionsMessage}`]: {
    padding: spacing(1, 2),
  },

  [`& .${classes.singleValue}`]: {
    fontSize: 16,
  },

  [`& .${classes.placeholder}`]: {
    position: 'absolute',
    color: COLORS.white[80],
    left: 2,
    fontSize: 16,
  },

  [`& .${classes.placeholderDisabled}`]: {
    position: 'absolute',
    left: 2,
    fontSize: 16,
    color: COLORS.white[20],
  },

  [`& .${classes.paper}`]: {
    position: 'absolute',
    zIndex: 5,
    marginTop: spacing(),
    left: 0,
    right: 0,
  },

  [`& .${classes.divider}`]: {
    height: spacing(2),
  },
}));

export interface StyledReactSelectProps<Multi extends boolean> extends SelectProps<SelectOption, Multi, GroupBase<SelectOption>> {
  nullable?: boolean;
  matchFrom?: 'start' | 'any';
}

const NoOptionsMessage = (props: any) => {
  return (
    <Typography color="textSecondary" className={props.selectProps.classes.noOptionsMessage} {...props.innerProps}>
      {props.children}
    </Typography>
  );
};

const inputComponent = React.forwardRef((props, ref) => {
  return <div {...props} ref={ref as React.LegacyRef<HTMLDivElement>} />;
});

const Control = (props: any) => {
  return (
    <TextField
      fullWidth={true}
      InputProps={{
        inputComponent,
        inputProps: {
          className: props.selectProps.classes.input,
          ref: props.innerRef,
          children: props.children,
          ...props.innerProps,
        },
      }}
      variant="standard"
      {...props.selectProps.textFieldProps}
    />
  );
};

const Option = (props: any) => {
  return (
    <MenuItem
      ref={props.innerRef}
      selected={props.isFocused}
      component="div"
      style={{
        fontWeight: props.isSelected ? 500 : 400,
      }}
      {...props.innerProps}
    >
      {props.children}
    </MenuItem>
  );
};

const Placeholder = (props: any) => {
  return (
    <Typography
      color="textSecondary"
      className={props.selectProps.disabled ? props.selectProps.classes.placeholderDisabled : props.selectProps.classes.placeholder}
      {...props.innerProps}
    >
      {props.children}
    </Typography>
  );
};

const SingleValue = (props: any) => {
  return (
    <Typography className={props.selectProps.classes.singleValue} {...props.innerProps}>
      {props.children}
    </Typography>
  );
};

const ValueContainer = (props: any) => {
  return <div className={props.selectProps.classes.valueContainer}>{props.children}</div>;
};

const MultiValue = (props: any) => {
  let className = `${props.selectProps.classes.chip}`;

  if (props.isFocused) {
    className += ` ${props.selectProps.classes.chipFocused}`;
  }

  return (
    <Chip
      tabIndex={-1}
      label={props.children}
      className={className}
      onDelete={props.removeProps.onClick}
      deleteIcon={<CancelIcon {...props.removeProps} />}
    />
  );
};

const Menu = (props: any) => {
  return (
    <Paper square={true} className={props.selectProps.classes.paper} {...props.innerProps}>
      {props.children}
    </Paper>
  );
};

const MenuList = (props: any) => {
  const makeOptions = useMemo(() => {
    return React.Children.map(props.children, (child: any) => {
      const { onMouseMove, onMouseOver, ...rest } = child.props.innerProps || {};
      const tempProps = { ...child.props, ...{ innerProps: rest } };

      return <Option {...tempProps} key={rest.id} />;
    });
  }, [props.children]);

  return <SelectComponents.MenuList {...props}>{makeOptions}</SelectComponents.MenuList>;
};

const components = {
  Control,
  Menu,
  MultiValue,
  NoOptionsMessage,
  Option,
  Placeholder,
  SingleValue,
  ValueContainer,
  MenuList,
};

type Props<Multi extends boolean> = StyledReactSelectProps<Multi>;

const StyledReactSelect = <Multi extends boolean>(props: Props<Multi>) => {
  const { nullable, options, isDisabled, value, onChange, isMulti, placeholder, matchFrom } = props;
  const stylesObj: StylesConfig<SelectOption, Multi> = {
    indicatorSeparator: (base) => ({ ...base, width: 0 }),
  };
  const filterOption = createFilter({ matchFrom: matchFrom, ignoreAccents: false });
  return (
    <StyledSelect>
      <Select
        isClearable={nullable}
        backspaceRemovesValue={nullable}
        options={options}
        isDisabled={isDisabled}
        value={value}
        onChange={onChange}
        className={isDisabled ? classes.dropdownDisabled : classes.dropdown}
        isMulti={isMulti}
        components={components}
        placeholder={placeholder}
        styles={stylesObj}
        {...{ classes: classes }}
        filterOption={filterOption}
      />
    </StyledSelect>
  );
};

export default StyledReactSelect;
